import { useEffect, useState, type FormEvent } from "react";
import type { EmailSettingsPublic } from "@tih/shared";
import {
  ApiError,
  adminFetchSettingsEmail,
  adminPutSettingsEmail,
  adminTestSettingsEmail,
} from "../../../api/client";

const EMPTY: EmailSettingsPublic = {
  host: "",
  port: 587,
  user: "",
  fromEmail: "",
  secure: false,
  passwordSet: false,
};

export function AdminSettingsEmailPage() {
  const [form, setForm] = useState(EMPTY);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await adminFetchSettingsEmail();
      setForm(data);
      setPassword("");
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await adminPutSettingsEmail({
        host: form.host.trim(),
        port: form.port,
        user: form.user.trim(),
        fromEmail: form.fromEmail.trim(),
        secure: form.secure,
        ...(password.trim() ? { password: password.trim() } : {}),
      });
      setForm(data);
      setPassword("");
      setSuccess("Email settings saved.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save email settings");
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminTestSettingsEmail();
      setSuccess(`Test email sent to ${res.sentTo}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send test email");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div style={{ opacity: 0.5 }}>Loading…</div>;
  }

  return (
    <div>
      <section className="admin-card">
        <h2 className="admin-card-title">SMTP configuration</h2>
        <p className="admin-card-sub">
          Used for subscriber broadcasts, attendance reports, and test emails. Password is encrypted in the database;
          leave blank when saving to keep the existing password.
        </p>

        <form onSubmit={(e) => void save(e)} className="admin-blog-form">
          <div className="admin-blog-form-fields">
            <label>
              <span>SMTP host</span>
              <input
                value={form.host}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
                placeholder="smtp.example.com"
                required
              />
            </label>
            <label>
              <span>Port</span>
              <input
                type="number"
                min={1}
                max={65535}
                value={form.port}
                disabled={busy}
                onChange={(e) => {
                  const port = Number(e.target.value);
                  setForm((f) => ({
                    ...f,
                    port,
                    secure: port === 465 ? true : f.secure,
                  }));
                }}
                required
              />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={form.secure}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, secure: e.target.checked }))}
              />
              <span>Use TLS/SSL (typical for port 465)</span>
            </label>
            <label>
              <span>Username</span>
              <input
                value={form.user}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, user: e.target.value }))}
                required
              />
            </label>
            <label>
              <span>From address</span>
              <input
                type="email"
                value={form.fromEmail}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, fromEmail: e.target.value }))}
                placeholder="hello@togetherinheaven.org"
                required
              />
            </label>
            <label>
              <span>
                Password{" "}
                {form.passwordSet ? (
                  <em style={{ fontWeight: 400, opacity: 0.7 }}>(leave blank to keep current)</em>
                ) : (
                  <em style={{ fontWeight: 400, opacity: 0.7 }}>(required on first save)</em>
                )}
              </span>
              <input
                type="password"
                value={password}
                disabled={busy}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </label>
          </div>
          <div className="admin-blog-form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Saving…" : "Save email settings"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy || !form.passwordSet}
              onClick={() => void sendTest()}
            >
              {busy ? "Sending…" : "Send test email"}
            </button>
          </div>
        </form>
      </section>

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12 }}>
          {success}
        </div>
      )}
    </div>
  );
}
